import React, { useEffect, useState, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import {
  Header,
} from '@backstage/core-components';
import 'ag-grid-enterprise';
import Loader from '../loader/Loader';
import { discoveryApiRef, useApi, fetchApiRef, identityApiRef } from '@backstage/core-plugin-api';
import '../NoteCustomstyle.css';

// Material UI
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';

const S3Buckets = () => {
  const gridRef = useRef<any>(null);
  const containerStyle = useMemo(() => ({ width: "100%", height: "100vh" }), []);
  const gridStyle = useMemo(() => ({ height: 'calc(-200px + 80vh)', width: "100%", marginTop: '50px' }), []);
  const [rowData, setRowdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState('');
  const [role, setRole] = useState<string>('');   // role state
  const [tenantName, settenantName] = useState<string>(''); // tenant dropdown state
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef)
  const [tenantsFromDb, setTenantsFromDb] = useState<any[]>([]);

  const fetchTenantsFromDb = async () => {
    try {
      const baseUrl = await discoveryApi.getBaseUrl('user');
      const response = await fetchApi.fetch(`${baseUrl}/tenants`);
      if (!response.ok) throw new Error("Failed to fetch tenants");
      return await response.json();
    } catch (e) {
      console.error("Error fetching tenants:", e);
      return [];
    }
  };

  const fetchTenant = async () => {
    try {
      const details = await identityApi.getBackstageIdentity();
      const id = details.ownershipEntityRefs.find(ref =>
        ref.startsWith('tenantId:')
      )?.split(':')[1] || null;

      console.log("ID ---------", id)
      return id
    } catch (err) {
      console.error('Failed to get tenantId:', err);
    }
  }; 


  useEffect(() => {
    const fetchData = async () => {
      try {
        const tenants = await fetchTenantsFromDb();
        const id = await fetchTenant()
        console.log(`orgName:${id}`)
        const tenantOrgName = localStorage.getItem(`orgName:${id}`);
        console.log('tenantOrgName------',tenantOrgName)
        if (tenantOrgName) setOrg(tenantOrgName);
        setTenantsFromDb(tenants);
        const baseurl = await discoveryApi.getBaseUrl('user');
        let data: any = [];

        try {
          const fetchresponse = await fetchApi.fetch(`${baseurl}/s3`, { method: 'POST' });
          if (!fetchresponse.ok) {
            const errorText = await fetchresponse.text();
            throw new Error(`API error: ${fetchresponse.status} - ${errorText}`);
          }
          const response = await fetchresponse.json();
          data = response.data || response;
          setRole(response.role || ""); // store role if backend sends it
          console.log('Fetched data:', data);
        } catch (error) {
          console.error('Fetch or JSON parse error:', error);
        }

        const rowsdata: any = formatData(data);
        setRowdata(rowsdata);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };
    fetchData();

    let orgName = localStorage.getItem('orgName');
    if (orgName) setOrg(orgName);
  }, []);

  const gigabytesToMBFormatter = (params: any) => {
    if (params.value == null || isNaN(params.value)) {
      return '';
    }
    else {
      const gigabytes = parseFloat(params.value);
      const megabytes = gigabytes * 1024;
      return megabytes.toFixed(2);
    }
  };

  const columnDefs = useMemo(() => {
    const baseCols = [
    {
      headerName: "Tenant Name", field: "tenantName", headerTooltip: "Tenant Name", rowGroup: true
    },
    {
      headerName: "Account Id", field: "accountId", headerTooltip: "Account Id", rowGroup: true,
      valueFormatter: params => {
        if (params.node.rowIndex === params.api.getDisplayedRowCount() - 1 && !params.value) {
          return 'Total';
        }
        return params.value;
      }
    },
    { headerName: "Bucket name", field: "bucketName", headerTooltip: "Bucket name" },
    { headerName: "Storage Class", field: "storageClass", headerTooltip: "Storage Class" },
    { headerName: "Storage size (MB)", field: "size", headerTooltip: "Storage size", valueFormatter: gigabytesToMBFormatter },
    {
      headerName: "Cost in $(per GB per month)", hide:true, field: "cost", headerTooltip: "Cost in $(per GB per month)", aggFunc: 'sum', valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value)) return value.toFixed(2);
      },
    },
    {
      headerName: "Potential cost saving in $ (per GB per month)", field: "potentialCostSaving", headerTooltip: "Potential cost saving in $ (per GB per month)", aggFunc: 'sum',
      valueFormatter: params => {
        const value = parseFloat(params.value);
        if (!isNaN(value)) return value.toFixed(2);
      },
    },
  ];
  
  return baseCols.map(col =>
    col.field === "tenantName"
      ? { ...col, hide: role != "SuperAdmin", rowGroup: role != "SuperAdmin" ? false : col.rowGroup }
      : col
  );
}, [role]);

  const defaultColDef = useMemo(() => ({
    flex: 1, filter: true, sortable: true, resizable: true,
    enableRowGroup: true, tooltipShowDelay: 0, tooltipInteraction: true, columnMenu: "new"
  }), []);

  const autoGroupColumnDef = {
    minWidth: 250,
    cellRendererParams: {
      footerValueGetter: (params: any) => {
        const isRootLevel = params.node.level === -1;
        return isRootLevel ? "Total" : " Sub Total";
      },
    },
  };

  const sideBar = {
    toolPanels: [
      { id: 'columns', labelDefault: 'Columns', labelKey: 'columns', iconKey: 'columns', toolPanel: 'agColumnsToolPanel' }
    ],
    defaultToolPanel: '',
  };

  const formatData = (data: any[]) => {
    return data.map(item => {
      const parsedData = item.data;
      return {
        tenantName: item.tenantName,  // include tenantName in row
        accountId: item.accountId,
        bucketName: item.bucketName,
        storageClass: parsedData.storageClass,
        size: parsedData.size,
        cost: parsedData.cost,
        potentialCostSaving: parsedData.potentialCostSaving
      };
    });
  };

  const gridOptions = { suppressContextMenu: true };

  // filter data based on selected tenant
  const filteredData = tenantName
    ? rowData.filter(row => String(row.tenantName) === String(tenantName))
    : rowData;

  // extract unique tenants for dropdown
  const tenants = [...new Set(rowData.map(r => r.tenantName))];
  console.log("Tenants in dropdown:", tenants);

  return (
    <div style={containerStyle}>
      <div>
        <Header title="S3 Buckets" subtitle={`${org}`} />
      </div>

      {/* Tenant dropdown for SuperAdmin */}
      {role === "SuperAdmin" && (
        <div style={{ display: "flex", justifyContent: "flex-end", margin: "10px 0" }}>
<FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
  <Select
    value={tenantName}
    onChange={(e) => settenantName(e.target.value)}
    displayEmpty
    MenuProps={{
      PaperProps: {
        sx: {
          // force vertical stacking
          '& .MuiList-root': {
            display: 'flex !important',
            flexDirection: 'column !important',
          },
          // force left-aligned text inside items
          '& .MuiMenuItem-root': {
            justifyContent: 'flex-start',
            pl: 2,
          },
        },
      },
    }}
  >
    <MenuItem value="">All Tenants</MenuItem>
    {tenantsFromDb.map((tenant) => (
      <MenuItem key={tenant.id} value={tenant.name}>
        {tenant.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>
        </div>
      )}

      {loading ? (<Loader />) : (
        <div>
          <div className="ag-theme-quartz resourcecost" style={{ ...gridStyle, marginTop: 10 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={columnDefs}
              autoGroupColumnDef={autoGroupColumnDef}
              rowGroupPanelShow={"always"}
              sideBar={sideBar}
              defaultColDef={defaultColDef}
              groupIncludeFooter={true}
              groupIncludeTotalFooter={true}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              gridOptions={gridOptions}
            />
          </div>
          <div className="recommended-action-container">
            <div className="scroll-container">
              <strong>Recommended Action</strong>
              <ul>
                <li>
                  <strong>Enable Intelligent Tiering in Amazon S3</strong>
                  <p>S3’s Intelligent Tiering automatically moves data to the most cost-effective storage class based on access patterns, without manual intervention. This can help reduce costs but comes with additional fees:</p>
                  <ul>
                    <li>Monitoring Costs: $0.0025 per 1,000 objects per month.</li>
                    <li>Transition Costs: $0.01 per 1,000 objects for shifting between storage classes.</li>
                    <li>Infrequent Access (IA) Retrieval: Costs $0.01 per GB, which is 4X cheaper than Standard storage. IA is ideal for data accessed infrequently or for disaster recovery.</li>
                  </ul>
                  <p>Considerations:</p>
                  <ul>
                    <li>Intelligent Tiering may or may not save money depending on your usage patterns.</li>
                    <li>Lower storage tiers have tradeoffs like reduced replications, lower SLA terms, and longer retrieval times.</li>
                  </ul>
                </li>
              </ul>
            </div>

          </div>
          <div className="note-container">
            <p>
              <strong>Note*</strong><span className="note-text">: The current storage cost is set at $0.023 per GB per month, which reflects the typical rate for S3 Standard storage in many AWS regions. The potential cost saving is calculated by estimating the cost if the data were moved to a cheaper storage class, such as S3 Glacier or S3 Infrequent Access, where the storage rate is assumed to be $0.00099 per GB per month.</span>
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default S3Buckets;
